import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAppStore } from './useAppStore';
import { useImprintStore } from './useImprintStore';
import { useSelectionStore } from './useSelectionStore';
import { useNewImprintStore } from './useNewImprintStore';
import { useCalendarStore } from './useCalendarStore';
import { useDesignOnlineStore } from '@/features/design/stores/useDesignOnlineStore';

// Import utilities
import { 
    calculateAllFees,
    getUniqueColorsCount,
    hasPantoneColor,
    hasUploadedImage
} from '../utils/feeCalculator.js';

import {
    formatColorName,
    renameSize,
    buildProductName,
    buildProductSKU,
    getProductWeight,
    buildItemMetaData,
    getColorProductId
} from '../utils/productMetadata.js';

import {
    buildSendArtworkPayload,
    buildPreviousArtworkPayload,
    buildCalendarCartPayload,
    combineMultipleImprintsAsPrefixed, combineMultipleImprintsAsArray
} from '../utils/payloadBuilders.js';

import {
    validateAddToCart,
    validateCartLocalStorageVariables,
    setMinQtyToStorage
} from '../utils/cartValidation.js';

// Import composable
import { useCartAPI } from '../composables/useCartAPI.js';

/**
 * Add to Cart Store (REFACTORED)
 * 
 * Thin orchestration layer that coordinates:
 * 1. Validation (via cartValidation.js)
 * 2. Fee calculation (via feeCalculator.js)
 * 3. Payload building (via transform methods + payloadBuilders.js)
 * 4. Product metadata (via productMetadata.js)
 * 5. API calls (via useCartAPI.js composable)
 * 
 * Heavy logic moved to utilities for better maintainability.
 */
export const useAddToCartStore = defineStore('addToCart', {
    state: () => ({
        // Loading state
        loader: false,
        addToCartStatus: false,
        
        // Correlation ID for tracking
        _corrId: null,
        
        // Validation results
        validationResult: {
            hasError: false,
            errors: []
        },
        
        // Fees and prices
        runningFeePerItem: 0,
        perItemFee: {
            setupFee: 0,
            proof_fee: 0
        },
        
        // Cart state (determines fee order)
        cartState: false, // false = fee first, true = items first
        stepForAddAssociatedFee: 'first',
        
        // Tracking cart items responses
        responsedCartItems: {},
        
        // Price data (gathered from stores)
        priceData: {
            tier: [],
            tierMSRP: 0,
            priceByQty: 0,
            priceSizeUpcharge: 0,
            priceColorUpcharge: 0,
            setup_fee: 0,
            proof_fee: 0,
            image_fee: 0,
            customColourFee: 0,
            running_fee: 0
        },
        
        // Bundle product cleared flag
        bundleProductCleared: false,
        
        // Logs for debugging
        logs: [],
        
        // Cart object (reactive reference)
        cartObject: {},
    }),

    getters: {
        /**
         * Check if Add to Cart is ready
         */
        isReadyToAddToCart(state) {
            const appStore = useAppStore();
            const selectionStore = useSelectionStore();
            const imprintStore = useImprintStore();
            
            // Check if main product ID exists
            if (!appStore.mainProductID) return false;

            if (appStore.isOutOfStock) return false;
            
            // Check if imprint method is selected
            if (!imprintStore.selectedImprintMethod.sku) return false;
            
            // Check if there are color-size lines (for default mode)
            if (appStore.applicationType === 'default') {
                const imprintKey = imprintStore.selectedImprintMethod.name;
                const lines = selectionStore.getLinesByImprintKey(imprintKey);
                if (!lines || lines.length === 0) return false;
            }
            
            // Check if calendar products exist (for calendar mode)
            if (appStore.applicationType === 'calendar') {
                if (!appStore.calendar.productList || appStore.calendar.productList.length === 0) {
                    return false;
                }
            }
            
            return true;
        }
    },

    actions: {
        /**
         * Add log entry for debugging
         */
        addLog({ type, message }) {
            const timestamp = new Date().toISOString();
            const logEntry = {
                timestamp,
                type, // 'debug', 'error', 'critical'
                message
            };
            
            this.logs.push(logEntry);
            
            // Console log based on type
            if (type === 'critical') {
                console.error('[AddToCart]', message);
            } else if (type === 'error') {
                console.warn('[AddToCart]', message);
            } else {
                console.log('[AddToCart]', message);
            }
        },

        /**
         * Check if current artwork method is "new" (Start New Imprint)
         */
        isNewArtworkMethod() {
            const imprintStore = useImprintStore();
            return imprintStore.selectedArtworkMethod === 'new';
        },

        /**
         * Build complete cart payload using transform methods and utilities
         * 
         * This method orchestrates:
         * 1. Get imprint payload from appropriate transform method
         * 2. Build cart items with product metadata
         * 3. Calculate and apply fees
         * 4. Return complete payload for backend
         */
        buildCartPayload() {
            const appStore = useAppStore();
            const imprintStore = useImprintStore();
            const selectionStore = useSelectionStore();
            const newImprintStore = useNewImprintStore();
            
            console.log('[AddToCart] buildCartPayload() - Building cart payload');
            
            // CHECK FOR CALENDAR MODE FIRST
            if (appStore.isCalendarMode) {
                console.log('[AddToCart] Calendar mode detected, using calendar payload builder');
                
                // Get calendar store
                const calendarStore = useCalendarStore();
                
                // Get imprint payload based on artwork method
                const artworkMethod = imprintStore.selectedArtworkMethod;
                let imprintPayload = null;
                
                if (artworkMethod === 'new') {
                    imprintPayload = newImprintStore.transformToCartPayload(null, this.priceData);
                } else if (artworkMethod === 'send') {
                    const currentImprint = newImprintStore.currentImprint;
                    imprintPayload = buildSendArtworkPayload(currentImprint, imprintStore, this.priceData);
                } else if (artworkMethod === 'previous') {
                    const currentImprint = newImprintStore.currentImprint;
                    imprintPayload = buildPreviousArtworkPayload(currentImprint, imprintStore, this.priceData);
                } else if (artworkMethod === 'design') {
                    // Design Online in calendar mode - same logic as default mode
                    const designOnlineStore = useDesignOnlineStore();

                    if (!designOnlineStore.isReady) {
                        console.error('[AddToCart] Calendar + Design Online: store not ready');
                        return null;
                    }

                    const props = designOnlineStore.lineItemProperties;
                    if (!props || !props.token_id) {
                        console.error('[AddToCart] Calendar + Design Online: missing token_id');
                        return null;
                    }

                    imprintPayload = {
                        imprint_lines: {},
                        properties: {
                            '_token_id': props.token_id,
                            '_pdf_preview_url': props.pdf_preview_url || '',
                            '_print_ready_file_url': props.print_ready_file_url || '',
                            '_design_url': designOnlineStore.editUrl || '',
                            '_preview_image': props.preview_image || '',
                            'Imprint Method': 'Design Online',
                            '_Imprint Method SKU': imprintStore.selectedImprintMethod?.sku || 'DESIGN_ONLINE',
                            '_price_details': {
                                tier: this.priceData.tier || [],
                                tierMSRP: this.priceData.tierMSRP || 0,
                                priceByQty: this.priceData.priceByQty || 0,
                                priceSizeUpcharge: 0,
                                priceColorUpcharge: 0,
                                setup_fee: 0,
                                proof_fee: 0,
                                image_fee: 0,
                                customColourFee: 0,
                                running_fee: 0
                            }
                        }
                    };

                    console.log('[AddToCart] Calendar + Design Online payload created:', imprintPayload);
                }
                
                if (!imprintPayload) {
                    console.error('[AddToCart] No imprint payload generated for calendar mode, artworkMethod:', artworkMethod);
                    return null;
                }
                
                // ✅ FIX: For calendar mode with 'new' artwork, we need to combine all imprints
                // just like we do for default mode
                if (artworkMethod === 'new') {
                    const allImprintsPayloads = [];
                    
                    console.log('[AddToCart] Calendar mode: Processing multiple imprints, total:', newImprintStore.imprints.length);
                    
                    // For additional location fee: calendar uses calendarStore.totalQuantity
                    const calendarTotalQty = calendarStore.totalQuantity;

                    for (let i = 0; i < newImprintStore.imprints.length; i++) {
                        const singleImprintPayload = newImprintStore.transformToCartPayload(i, this.priceData);
                        if (singleImprintPayload) {
                            // Inject additional_location_fee for 2nd+ imprints
                            if (i >= 1) {
                                const addLocFeePerPiece = imprintStore.getAdditionalLocationFeePerPiece(calendarTotalQty);
                                const addLocFeeTotal = addLocFeePerPiece * calendarTotalQty;
                                singleImprintPayload.fees = {
                                    ...singleImprintPayload.fees,
                                    additional_location_fee: addLocFeeTotal,
                                    additional_location_fee_qty: calendarTotalQty,
                                    additional_location_fee_unit_price: addLocFeePerPiece,
                                    additional_location_fee_tiers: imprintStore.currentAdditionalLocationFeeTiers
                                };
                                console.log(`[AddToCart] Calendar imprint ${i}: additional_location_fee=${addLocFeeTotal}`);
                            } else {
                                singleImprintPayload.fees = {
                                    ...singleImprintPayload.fees,
                                    additional_location_fee: 0,
                                    additional_location_fee_qty: 0,
                                    additional_location_fee_unit_price: 0,
                                    additional_location_fee_tiers: []
                                };
                            }
                            allImprintsPayloads.push({
                                imprintKey: i,
                                payload: singleImprintPayload
                            });
                            console.log(`[AddToCart] Calendar imprint ${i} payload:`, singleImprintPayload);
                        }
                    }
                    
                    if (allImprintsPayloads.length > 0) {
                        // Combine all imprints into one payload
                        imprintPayload = combineMultipleImprintsAsArray(allImprintsPayloads);
                        console.log('[AddToCart] Calendar combined payload:', imprintPayload);

                        if (imprintPayload?.properties?.["_price_details"]) {
                            const runningFeeMultiplier = imprintPayload.fees?.running_fee_multiplier || 0;
                            const runningFeeRate = imprintPayload.fees?.running_fee_rate || 0;
                            imprintPayload.properties["_price_details"].running_fee_qty = runningFeeMultiplier * calendarTotalQty;
                            imprintPayload.properties["_price_details"].running_fee_unit_price = runningFeeRate;
                        }
                    }
                }

                // Use calendar payload builder - pass FULL imprintPayload, not just properties
                return buildCalendarCartPayload(
                    appStore,
                    imprintStore,
                    newImprintStore,
                    calendarStore,
                    imprintPayload  // ✅ Pass full payload with imprint_lines
                );
            }
            
            // DEFAULT MODE (apparel with colors/sizes)
            // 1. Get imprint data from transform method based on artwork method
            const artworkMethod = imprintStore.selectedArtworkMethod;
            let imprintPayload = null;
            
            if (artworkMethod === 'new') {
                // Start New Imprint - uses transform method with fee calculation
                console.log('[AddToCart] Calling transformToCartPayload with priceData:', this.priceData);
                imprintPayload = newImprintStore.transformToCartPayload(null, this.priceData);
                console.log('[AddToCart] transformToCartPayload returned:', imprintPayload);

                const allImprintsPayloads = [];

                console.log('[AddToCart] Processing multiple imprints, total:', newImprintStore.imprints.length);

                // Проходимо по всіх imprints
                // For additional location fee: need totalQty to pick the right tier
                const imprintKey = imprintStore.selectedImprintMethod.name;
                const totalQtyForFee = selectionStore.getTotalQtyByImprintKey(imprintKey);

                for (let i = 0; i < newImprintStore.imprints.length; i++) {
                    const singleImprintPayload = newImprintStore.transformToCartPayload(i, this.priceData);
                    if (singleImprintPayload) {
                        // Inject additional_location_fee for 2nd+ imprints
                        if (i >= 1) {
                            const addLocFeePerPiece = imprintStore.getAdditionalLocationFeePerPiece(totalQtyForFee);
                            const addLocFeeTotal = addLocFeePerPiece * totalQtyForFee;
                            singleImprintPayload.fees = {
                                ...singleImprintPayload.fees,
                                additional_location_fee: addLocFeeTotal,
                                additional_location_fee_qty: totalQtyForFee,
                                additional_location_fee_unit_price: addLocFeePerPiece,
                                // Pass full tier array so cart can recalculate dynamically on qty change
                                additional_location_fee_tiers: imprintStore.currentAdditionalLocationFeeTiers
                            };
                            console.log(`[AddToCart] Imprint ${i}: additional_location_fee=${addLocFeeTotal} (${addLocFeePerPiece}/pc × ${totalQtyForFee}qty)`);
                        } else {
                            singleImprintPayload.fees = {
                                ...singleImprintPayload.fees,
                                additional_location_fee: 0,
                                additional_location_fee_qty: 0,
                                additional_location_fee_unit_price: 0,
                                additional_location_fee_tiers: []
                            };
                        }
                        allImprintsPayloads.push({
                            imprintKey: i,
                            payload: singleImprintPayload
                        });
                        console.log(`[AddToCart] Imprint ${i} payload:`, singleImprintPayload);
                    }
                }

                if (allImprintsPayloads.length === 0) {
                    console.error('[AddToCart] No valid imprints found');
                    return null;
                }


                // Variant B: Array format
                // imprintPayload = combineMultipleImprintsAsArray(allImprintsPayloads);

                // Variant A: Prefixed properties  combineMultipleImprintsAsPrefixed
                imprintPayload = combineMultipleImprintsAsArray(allImprintsPayloads);

                console.log('[AddToCart] Combined payload for', allImprintsPayloads.length, 'imprints:', imprintPayload);

                if (!imprintPayload) {
                    console.error('[AddToCart] transformToCartPayload returned NULL');
                    console.error('[AddToCart] currentImprintKey:', newImprintStore.currentImprintKey);
                    console.error('[AddToCart] imprints:', newImprintStore.imprints);
                } else if (imprintPayload.properties?.["_price_details"]) {
                    // Running Fee qty is only knowable here, where the real product qty
                    // (totalQtyForFee) is available — transformToCartPayload() always uses
                    // totalQuantity=1 as a placeholder since it doesn't know product qty yet.
                    const runningFeeMultiplier = imprintPayload.fees?.running_fee_multiplier || 0;
                    const runningFeeRate = imprintPayload.fees?.running_fee_rate || 0;
                    imprintPayload.properties["_price_details"].running_fee_qty = runningFeeMultiplier * totalQtyForFee;
                    imprintPayload.properties["_price_details"].running_fee_unit_price = runningFeeRate;
                }

                
            } else if (artworkMethod === 'send') {
                // Send Us Artwork - uses utility builder
                const currentImprint = newImprintStore.currentImprint;
                imprintPayload = buildSendArtworkPayload(currentImprint, imprintStore, this.priceData);
                console.log('[AddToCart] Using Send Artwork payload:', imprintPayload);
                
            } else if (artworkMethod === 'previous') {
                // Use Previous Artwork - uses utility builder
                const currentImprint = newImprintStore.currentImprint;
                imprintPayload = buildPreviousArtworkPayload(currentImprint, imprintStore, this.priceData);
                console.log('[AddToCart] Using Previous Artwork payload:', imprintPayload);
                
            } else if (artworkMethod === 'design') {
                // ✅ Design Online - get from CustomizeProductSection transform
                console.log('[AddToCart] Design Online detected - getting payload from store');
                
                // Get Design Online store instance
                const designOnlineStore = useDesignOnlineStore();
                
                console.log('[AddToCart] Design Online store state:', {
                    stage: designOnlineStore.stage,
                    hasDesign: designOnlineStore.hasDesign,
                    isReady: designOnlineStore.isReady,
                    isApproved: designOnlineStore.isApproved,
                    tokenId: designOnlineStore.tokenId
                });
                
                // Check if design is ready
                if (!designOnlineStore.isReady) {
                    console.error('[AddToCart] Design Online: Not ready!');
                    console.error('[AddToCart] User must approve design by checking the checkbox');
                    return null;
                }
                
                // Get line item properties
                const props = designOnlineStore.lineItemProperties;
                
                if (!props || !props.token_id) {
                    console.error('[AddToCart] Design Online: Missing token_id');
                    return null;
                }
                
                // ✅ Build upcharge objects from selectionStore
                const sizeUpchargeObj = {};
                const colorUpchargeObj = {};
                
                // Get size upcharges from imprintStore
                if (imprintStore.sizeUpcharge) {
                    Object.keys(imprintStore.sizeUpcharge).forEach(size => {
                        if (imprintStore.sizeUpcharge[size] !== 0) {
                            sizeUpchargeObj[size] = imprintStore.sizeUpcharge[size];
                        }
                    });
                }
                
                // Get color upcharges from imprintStore
                if (imprintStore.colorUpcharge) {
                    Object.keys(imprintStore.colorUpcharge).forEach(colorKey => {
                        const colorData = imprintStore.colorUpcharge[colorKey];
                        if (colorData && colorData.price !== 0) {
                            colorUpchargeObj[colorKey] = {
                                price: colorData.price || 0,
                                price_type: colorData.price_type || ''
                            };
                        }
                    });
                }
                
                console.log('[AddToCart] Design Online upcharges:', {
                    sizeUpcharge: sizeUpchargeObj,
                    colorUpcharge: colorUpchargeObj
                });
                
                // Build payload structure (same as CustomizeProductSection.transformDesignOnlineToCart)
                imprintPayload = {
                    imprint_lines: {},
                    properties: {
                        // ✅ REQUIRED: Design Online specific
                        "_token_id": props.token_id,
                        "_pdf_preview_url": props.pdf_preview_url || '',
                        "_print_ready_file_url": props.print_ready_file_url || '',
                        
                        // ✅ OPTIONAL
                        "_design_url": designOnlineStore.editUrl || '',
                        "_preview_image": props.preview_image || '',
                        
                        // Standard
                        "Imprint Method": "Design Online",
                        "_Imprint Method SKU": imprintStore.selectedImprintMethod?.sku || 'DESIGN_ONLINE',
                        
                        // ✅ NEW: Size and Color upcharges
                        "_sizeUpcharge": sizeUpchargeObj,
                        "_colorUpcharge": colorUpchargeObj,
                        
                        // Price details (no fees for Design Online, but includes tier pricing)
                        "_price_details": {
                            tier: this.priceData.tier || [],
                            tierMSRP: this.priceData.tierMSRP || 0,
                            priceByQty: this.priceData.priceByQty || 0,
                            priceSizeUpcharge: this.priceData.priceSizeUpcharge || 0,
                            priceColorUpcharge: this.priceData.priceColorUpcharge || 0,
                            setup_fee: 0,
                            proof_fee: 0,
                            image_fee: 0,
                            customColourFee: 0,
                            running_fee: 0
                        }
                    }
                };
                
                console.log('[AddToCart] ✅ Design Online payload created:', imprintPayload);
            }
            
            if (!imprintPayload) {
                console.error('[AddToCart] No imprint payload generated for method:', artworkMethod);
                return null;
            }
            
            // 2. Update runningFeePerItem from payload fees (if available)
            if (imprintPayload.fees) {
                this.runningFeePerItem = imprintPayload.fees.runningFeePerItem || 0;
            }
            
            // 3. Build items with product metadata using utilities
            const imprintKey = imprintStore.selectedImprintMethod.name;
            const lines = selectionStore.getLinesByImprintKey(imprintKey);
            const groupID = this.getGroupID();
            const items = [];
            
            console.log('[AddToCart] Building items from', lines.length, 'color lines');
            
            lines.forEach(colorSizeLine => {
                for (const [size, qty] of Object.entries(colorSizeLine.selectedSizes)) {
                    if (qty > 0) {
                        // Check if this is a qty-only product (not apparel with sizes)
                        const isQtyOnly = size === 'qty';
                        const names = buildProductName(colorSizeLine, size, appStore, imprintStore);
                        // Build properties using ready-made imprint properties + product metadata
                        const properties = {
                            ...imprintPayload.properties,  // All imprint data ready!
                            
                            // Add product-specific properties using utilities
                            'Color': colorSizeLine.slectedColor.title,
                            // Only add Size if it's not a qty-only product
                            ...(isQtyOnly ? {} : { 'Size': size }),
                            '_Product name': names.order,
                            '_Cart product name':names.cart,
                            '_img_src': colorSizeLine.slectedColor.img_src,
                            '_mainProductId': appStore.mainProductID,
                            '_colorId': colorSizeLine.slectedColor.id,
                            '_productUrl': window.location.pathname,
                            '_groupID': groupID,
                            '_qtyIncrement': imprintStore.currentQtyIncrement,
                            '_runningFeePerItem': this.runningFeePerItem,
                            
                            // Product metadata from simple_array (using utilities)
                            '_Product Weight': getProductWeight(colorSizeLine, size, imprintStore, appStore, selectionStore),
                            '_Product Color': colorSizeLine.slectedColor.title,
                            '_Product SKU': buildProductSKU(colorSizeLine, size, appStore, imprintStore, selectionStore)
                        };
                        
                        items.push({
                            id: colorSizeLine.slectedColor.id,
                            quantity: qty,
                            properties: properties,
                            metaData: buildItemMetaData(
                                colorSizeLine,
                                size,
                                appStore,
                                imprintStore,
                                selectionStore,
                                this.priceData.priceByQty
                            )
                        });
                    }
                }
            });
            
            console.log('[AddToCart] Built', items.length, 'cart items');
            
            // 4. Get color product ID using utility
            const colorProductId = getColorProductId(lines, appStore);
            
            // 5. Return complete payload
            return {
                id: appStore.mainProductID,
                imprint_lines: imprintPayload.imprint_lines,
                items: items,
                colorProductID: colorProductId
            };
        },

        /**
         * Validate all data before adding to cart
         * Uses cartValidation.js utility
         */
        validate() {
            const appStore = useAppStore();
            const selectionStore = useSelectionStore();
            const imprintStore = useImprintStore();
            const newImprintStore = useNewImprintStore();
            
            // Use validation utility
            this.validationResult = validateAddToCart({
                appStore,
                selectionStore,
                imprintStore,
                newImprintStore
            });
            
            return this.validationResult;
        },

        /**
         * Gather price data from stores and calculate fees
         * 
         * This method:
         * 1. Gets tier pricing based on quantity
         * 2. Applies sale prices if applicable
         * 3. Calculates size/color upcharges
         * 4. Gets base fees from backend config
         * 
         * Note: Actual fees are calculated in transform methods/utilities
         * This just gathers the base pricing structure
         */
        gatherPriceData() {
            const imprintStore = useImprintStore();
            const selectionStore = useSelectionStore();
            
            const imprintKey = imprintStore.selectedImprintMethod.name;
            const totalQty = selectionStore.getTotalQtyByImprintKey(imprintKey);
            
            const sku = imprintStore.selectedImprintMethod.sku;
            const configurableData = imprintStore.fullJson?.configurable_array?.[sku];
            
            // Get tier pricing
            const tierPricing = configurableData?.prices_2?.tier_price || 
                               configurableData?.prices?.tier_price || 
                               [];
            
            // Find applicable tier based on quantity
            let applicableTier = tierPricing[0];
            for (const tier of tierPricing) {
                if (totalQty >= tier.QtyMin) {
                    applicableTier = tier;
                } else {
                    break;
                }
            }
            
            // Get price (check for sale price)
            let priceByQty = applicableTier?.price || 0;
            if (applicableTier?.sale_price) {
                const now = new Date();
                const effectiveDate = applicableTier.sale_price_effectivedate ? 
                    new Date(applicableTier.sale_price_effectivedate) : null;
                const terminationDate = applicableTier.sale_price_terminationdate ? 
                    new Date(applicableTier.sale_price_terminationdate) : null;
                
                if ((!effectiveDate || now >= effectiveDate) && 
                    (!terminationDate || now <= terminationDate)) {
                    priceByQty = applicableTier.sale_price;
                }
            }
            
            // Fallback to min price if no tier price
            if (priceByQty === 0) {
                priceByQty = configurableData?.prices_2?.min_price || 
                            configurableData?.prices?.min_price || 
                            configurableData?.prices_2?.sale_min_price ||
                            0;
            }
            
            // Calculate size and color upcharges
            let totalSizeUpcharge = 0;
            let totalColorUpcharge = 0;
            
            const lines = selectionStore.getLinesByImprintKey(imprintKey);
            lines.forEach(line => {
                const color = line.slectedColor;
                const colorName = color?.title?.toLowerCase();
                
                // Color upcharge
                if (colorName && imprintStore.colorUpcharge[colorName]) {
                    const colorUpchargeData = imprintStore.colorUpcharge[colorName];
                    totalColorUpcharge += colorUpchargeData.price || 0;
                }
                
                // Size upcharge
                if (line.selectedSizes) {
                    Object.keys(line.selectedSizes).forEach(size => {
                        const qty = line.selectedSizes[size];
                        if (qty > 0 && imprintStore.sizeUpcharge[size]) {
                            totalSizeUpcharge += imprintStore.sizeUpcharge[size] * qty;
                        }
                    });
                }
            });
            
            // Get base imprint fees from backend config
            const imprintData = configurableData?.configurable_2_data?.imprint_data || {};
            
            // Add is_special_tier flag to tier pricing
            const tierPricingWithFlags = tierPricing.map(tier => ({
                ...tier,
                is_special_tier: tier.is_special_tier || false
            }));
            
            const appStore = useAppStore();

            // Build price data structure
            // Note: setup_fee, running_fee, etc. will be overridden by transform methods
            this.priceData = {
                tier: tierPricingWithFlags,
                b2bTier: appStore.getB2BTierForSku(sku) ?? null,
                tierMSRP: configurableData?.prices_2?.msrp || configurableData?.prices?.msrp || 0,
                priceByQty: priceByQty,
                priceSizeUpcharge: totalSizeUpcharge,
                priceColorUpcharge: totalColorUpcharge,
                setup_fee: imprintData.setup_fee || 0,  // Base fee from backend
                proof_fee: imprintData.proof_fee || 0,  // Base fee from backend
                image_fee: imprintData.image_fee || 0,  // Base fee from backend
                customColourFee: 0,
                running_fee: imprintData.running_fee || 0  // Base fee from backend
            };
            
            // Store base fees for reference
            this.perItemFee.setupFee = this.priceData.setup_fee;
            this.perItemFee.proof_fee = this.priceData.proof_fee;
            
            return this.priceData;
        },

        /**
         * Get group ID from app store
         */
        getGroupID() {
            const appStore = useAppStore();
            if (!appStore.currentGroupID) {
                appStore.generateGroupID();
            }
            return appStore.currentGroupID;
        },

        /**
         * Main Add to Cart method
         * 
         * Orchestrates the complete flow:
         * 1. Generate correlation ID
         * 2. Validate all data
         * 3. Gather price data
         * 4. Build cart payload
         * 5. Send to backend and Shopify
         * 6. Handle redirect
         */
        async addToCart({ onAfterSuccess } = {}) {
            try {
                this.loader = true;
                const date = new Date();
                console.log(`[AddToCart] Start adding to cart: ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}:${date.getMilliseconds()}`);

                const appStore = useAppStore();
                const imprintStore = useImprintStore();
                const selectionStore = useSelectionStore();

                // Generate correlation ID for tracking
                this._corrId = `ATC-${Date.now()}-${Math.random().toString(16).slice(2,8)}`;
                this.addLog({
                    type: 'debug', 
                    message: `ATC start corrId=${this._corrId}; appType=${appStore.applicationType}; isEnvelop=${!!appStore.isEnvelopProduct}`
                });

                // Validate (skip for envelope products)
                const resultv = this.validate();
                console.log('[AddToCart] Validation result:', resultv);

                if (appStore.isEnvelopProduct) {
                    this.validationResult.hasError = false;
                }

                if (!this.validationResult.hasError) {
                    // Gather price data (needed for transform methods)
                    this.gatherPriceData();
                    
                    // Build cart payload using transform methods + utilities
                    const prepareDataToCart = this.buildCartPayload();
                    
                    if (!prepareDataToCart) {
                        this.loader = false;
                        this.addLog({
                            type: 'critical',
                            message: `${this._corrId} buildCartPayload() returned null - cannot proceed`
                        });
                        console.error('[AddToCart] Failed to build cart payload');
                        return;
                    }
                    
                    console.log('[AddToCart] Cart payload built successfully:', prepareDataToCart);

                    // Log payload summary for debugging
                    try {
                        const sumItems = (prepareDataToCart.items || []).map(i => ({
                            colorId: i.id, 
                            qty: i.quantity,
                            sku: i.metaData?.color_sku, 
                            price: i.metaData?.price
                        }));
                        this.addLog({
                            type: 'debug', 
                            message: `${this._corrId} BE payload summary: colorProductID=${prepareDataToCart.colorProductID}; items=${sumItems.length}; sample=${JSON.stringify(sumItems.slice(0,3))}`
                        });
                    } catch(e) {
                        console.warn('[AddToCart] Failed to log payload summary:', e);
                    }

                    // Build API data for backend
                    const apiData = {
                        'mode': appStore.applicationType,
                        'associatedFeeID': window.PXUTheme.theme_settings.associated_fee_id,
                        'placeholderImageGID': window.PXUTheme.theme_settings.placeholder_image_gid,
                        'shop': 'mapleleafpromotions',
                        'shop_domain': 'mapleleafpromotions.myshopify.com',
                        'isCustomerGuest': appStore.isCustomerGuest,
                        // customerId is passed so the backend can resolve the B2B company ID
                        // via the Shopify Admin API. b2bCompanyId is intentionally omitted —
                        // the backend derives it server-side and never trusts client input.
                        'customerId': document.getElementById('customer_id')?.textContent?.trim() || '',
                        'data': prepareDataToCart,
                        '_corrId': this._corrId
                    };

                    // Add envelope flag if needed
                    if (appStore.isEnvelopProduct) {
                        apiData.isEnvelopProduct = true;
                    }

                    // Override shop domain from config if available
                    if (window.PXUTheme.theme_settings.api_shop_domain) {
                        const domainStr = window.PXUTheme.theme_settings.api_shop_domain;
                        apiData.shop_domain = domainStr;
                        apiData.shop = domainStr.replace('.myshopify.com', '');
                    }
                    
                    this.addToCartStatus = true;

                    // Get total quantity for localStorage
                    const imprintKey = imprintStore.selectedImprintMethod.name;
                    const totalQty = selectionStore.getTotalQtyByImprintKey(imprintKey);

                    // Use Cart API composable for complete flow
                    const cartAPI = useCartAPI();
                    
                    // Convert reactive refs for composable
                    const cartObjectRef = ref(this.cartObject);
                    const responsedCartItemsRef = ref(this.responsedCartItems);
                    
                    // Extract fee SKUs from product imprint_data for correct fee SKU assignment
                    const imprintSku = imprintStore.selectedImprintMethod?.sku;
                    const imprintData = imprintSku
                        ? imprintStore.fullJson?.configurable_array?.[imprintSku]?.configurable_2_data?.imprint_data
                        : null;


                    // additional_location_fee_sku lives in the metafield tiers (field: "sku"),
                    // NOT in imprint_data — read it from currentAdditionalLocationFeeTiers.
                    const additionalLocationFeeSku =
                        imprintStore.currentAdditionalLocationFeeTiers?.[0]?.sku || 'ADDLOCRUN';

                    const feeSkus = {
                        setup_fee_sku: imprintData?.setup_fee_sku || '',
                        running_fee_sku: imprintData?.running_fee_sku || '',
                        image_fee_sku: imprintData?.image_fee_sku || '',
                        colour_fee_sku: imprintData?.colour_fee_sku || '',
                        proof_fee_sku: imprintData?.proof_fee_sku || '',
                        additional_location_fee_sku: additionalLocationFeeSku
                    };
                    console.log('[AddToCart] Fee SKUs from product data:', feeSkus);
                    
                    await cartAPI.completeAddToCart({
                        apiData,
                        corrId: this._corrId,
                        groupID: this.getGroupID(),
                        cartState: this.cartState,
                        cartObject: cartObjectRef,
                        responsedCartItems: responsedCartItemsRef,
                        feeSkus,
                        onSuccess: async () => {
                            // Update store with refs
                            this.cartObject = cartObjectRef.value;
                            this.responsedCartItems = responsedCartItemsRef.value;
                            this.addToCartStatus = false;
                            this.loader = false;
                            
                            // Get minQty from tier pricing
                            const minQty = this.priceData.tier[0]?.QtyMin || 0;
                            
                            // Save minimum quantity to localStorage
                            setMinQtyToStorage(this.getGroupID(), minQty, totalQty);

                            // Clear all stored data AFTER successful cart update (before redirect)
                            if (typeof onAfterSuccess === 'function') {
                                console.log('[AddToCart] Calling onAfterSuccess (clearAllData)...');
                                onAfterSuccess();
                            }

                            // CS-316: catch any broken-cart state (this attempt's own
                            // partial failures are already handled by completeAddToCart's
                            // own rollback — this also catches anything left over from an
                            // earlier session) right here, on the product page, BEFORE
                            // redirecting — the earliest possible point, rather than
                            // waiting for the next product-page load or relying solely on
                            // the cart page's own check.
                            try {
                                if (window.__cartRecovery) {
                                    const result = await window.__cartRecovery.recoverCart();
                                    if (result?.broken) {
                                        console.warn('[AddToCart] Recovered broken cart before redirect:', result);
                                    }
                                }
                            } catch (e) {
                                console.error('[AddToCart] cart recovery check failed (non-blocking):', e);
                            }
                            
                            // Wait for localStorage to be ready, then redirect
                            this.waitForLocalStorageAndRedirect();
                        },
                        onError: (error) => {
                            this.loader = false;
                            this.addToCartStatus = false;
                            this.addLog({
                                type: 'critical',
                                message: `${this._corrId} Add to cart failed: ${error.message}`
                            });

                            // CS-316: fire-and-forget check even on failure —
                            // completeAddToCart's own rollback already removed THIS
                            // attempt's partial lines, but this is a cheap extra safety
                            // net for anything else still lingering.
                            if (window.__cartRecovery) {
                                window.__cartRecovery.recoverCart().catch(() => { /* never block on this */ });
                            }
                        }
                    });
                    
                } else {
                    // Validation failed
                    this.loader = false;
                    console.error('[AddToCart] Validation failed:', this.validationResult.errors);
                }
                
            } catch (e) {
                this.loader = false;
                this.addToCartStatus = false;
                console.error('[AddToCart] Exception:', e);
                this.addLog({
                    type: 'critical', 
                    message: `${this._corrId} Exception in addToCart(): ${e?.message || e}`
                });
            }
        },

        /**
         * Wait for localStorage to be ready, then redirect to cart
         * 
         * This ensures both minQty and cartItems are saved before redirecting
         */
        waitForLocalStorageAndRedirect() {
            const maxIteration = 50;
            let counter = 0;
            
            const checkAndRedirect = () => {
                counter++;

                // Check both minQty and cartItems
                let hasMinQty = false;
                try {
                    hasMinQty = validateCartLocalStorageVariables();
                } catch (e) {
                    console.warn('[AddToCart] validateCartLocalStorageVariables failed', e);
                    hasMinQty = false;
                }

                const hasCartItems = localStorage.getItem('cartItems') !== null;
                
                if (hasMinQty && hasCartItems) {
                    console.log('[AddToCart] localStorage ready, redirecting to cart');
                    window.location.href = '/cart';
                } else if (counter < maxIteration) {
                    setTimeout(checkAndRedirect, 100);
                } else {
                    console.error('[AddToCart] Redirect timeout - missing localStorage:', {
                        hasMinQty,
                        hasCartItems
                    });
                    // ✅ Do not block (Datadog Synthetics + better UX)
                    // Fallback: go to cart anyway; cart page can handle missing values gracefully.
                    try { localStorage.setItem('atc_redirect_timeout', '1'); } catch(e) {
                        console.warn('[AddToCart] localStorage blocked, skipping flag', e);
                    }
                    window.location.href = '/cart';
                }
            };
            
            setTimeout(checkAndRedirect, 100);
        }
    }
});
